import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@zep-us/zus-icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import type { ClassroomDashboardRes } from '@/api/gen/_model/classroomDashboardRes';
import { getClassGroupDetail as getClassroomDetail, useGetJoinedClassrooms, useGetOwnedClassrooms } from '@/api/gen/classroom/classroom.gen';
import { openClassroomShareModal } from '@/components/modals/ShareModal';
import { useViewport } from '@/hooks';
import { ProfileTitleWithMenus } from '@/module/profile/share/ProfileTitleWithMenus';
import { type ClassroomItem, CreateClassroomCard, EnterClassroomCard, OwnerClassroomCard, StudentClassroomCard } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomCards';
import { CreateClassroomModal, type CreatedClassroom, CreatedClassroomModal, EditClassroomNameModal } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomCreateModals';
import { ClassroomListSearchToolbar, type ClassroomListSortMode } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomListSearchToolbar';
import { CLASSROOM_LIST_TABS, ClassroomListTab, ClassroomListTabs } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomListTabs';
import { ClassroomListGridSkeleton } from '@/pages/classrooms/shared/components/ClassroomLoadingSkeletons';
import { useDeleteClassroom } from '@/pages/classrooms/shared/hooks';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { usePointReleaseModal } from '@/shared/features/classroom/hooks';
import { trackClassroomEvent } from '@/shared/features/classroom/utils';
import { useCanCreateClassroom } from '@/shared/hooks';
import { selectData } from '@/shared/utils';
import { Button, confirmDialog } from '@/ui_v3';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
function toClassroomItem(classroom: ClassroomDashboardRes, isClassroomOwner: boolean, useOwnerMobileEntry: boolean): ClassroomItem {
  return {
    id: classroom.classGroupId,
    name: classroom.name,
    subtitle: classroom.ownerName,
    ownerAvatarData: classroom.ownerAvatarData,
    memberCount: classroom.memberCount ?? 0,
    isOwner: isClassroomOwner,
    href: CLASSROOM_ROUTES.detail(classroom.classGroupId, isClassroomOwner, {
      isMobile: useOwnerMobileEntry
    })
  };
}

// showOwnedTab: '만든 클래스' 탭 노출 여부. 학생 계정은 과거에 만든 클래스가 있어도
// 학생 persona 로만 다루기 위해 owner 클래스 노출을 숨긴다.
// defaultToOwnedTab: 진입 시 기본 탭을 만든 클래스로 둘지.
function useDashboardTabState(showOwnedTab: boolean, defaultToOwnedTab: boolean) {
  const defaultActiveTab = defaultToOwnedTab ? ClassroomListTab.OWNED_CLASSROOMS : ClassroomListTab.JOINED_CLASSROOMS;
  const [activeTab, setActiveTab] = useState<ClassroomListTab>(defaultActiveTab);
  const [hasSelectedTab, setHasSelectedTab] = useState(false);
  const tabs = useMemo(() => showOwnedTab ? CLASSROOM_LIST_TABS : CLASSROOM_LIST_TABS.filter(tab => tab.value === ClassroomListTab.JOINED_CLASSROOMS), [showOwnedTab]);
  useEffect(() => {
    if (hasSelectedTab) return;
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab, hasSelectedTab]);
  const handleChangeTab = (tab: ClassroomListTab) => {
    setHasSelectedTab(true);
    setActiveTab(tab);
  };
  return {
    activeTab,
    tabs,
    handleChangeTab
  };
}
function sortClassroomItems(items: ClassroomItem[], sortMode: ClassroomListSortMode): ClassroomItem[] {
  if (sortMode === 'name') {
    return [...items].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }
  return items;
}
function useClassroomItems({
  activeTab,
  searchText,
  sortMode
}: {
  activeTab: ClassroomListTab;
  searchText: string;
  sortMode: ClassroomListSortMode;
}) {
  const {
    isMobile
  } = useViewport();
  const ownedQuery = useGetOwnedClassrooms(undefined, {
    query: {
      select: selectData,
      enabled: activeTab === ClassroomListTab.OWNED_CLASSROOMS,
      staleTime: 30_000,
      // 응답에 서버가 join한 ownerAvatarData 가 들어있어, 페이지 진입 시 항상
      // 최신을 받아 아바타/프로필 변경(어느 경로든)을 반영한다.
      refetchOnMount: 'always'
    }
  });
  const joinedQuery = useGetJoinedClassrooms(undefined, {
    query: {
      select: selectData,
      enabled: activeTab === ClassroomListTab.JOINED_CLASSROOMS,
      staleTime: 30_000,
      refetchOnMount: 'always'
    }
  });
  const ownedItems = useMemo<ClassroomItem[]>(() => (ownedQuery.data ?? []).map(classroom => toClassroomItem(classroom, true, isMobile)), [isMobile, ownedQuery.data]);
  const joinedItems = useMemo<ClassroomItem[]>(() => (joinedQuery.data ?? []).map(classroom => toClassroomItem(classroom, false, isMobile)), [isMobile, joinedQuery.data]);
  const normalizedSearchText = searchText.trim().toLowerCase();
  const ownedClassrooms = useMemo(() => {
    const filtered = normalizedSearchText ? ownedItems.filter(item => item.name.toLowerCase().includes(normalizedSearchText)) : ownedItems;
    return sortClassroomItems(filtered, sortMode);
  }, [ownedItems, normalizedSearchText, sortMode]);
  const joinedClassrooms = useMemo(() => {
    const filtered = normalizedSearchText ? joinedItems.filter(item => item.name.toLowerCase().includes(normalizedSearchText)) : joinedItems;
    return sortClassroomItems(filtered, sortMode);
  }, [joinedItems, normalizedSearchText, sortMode]);
  const activeQuery = activeTab === ClassroomListTab.OWNED_CLASSROOMS ? ownedQuery : joinedQuery;
  return {
    ownedClassrooms,
    joinedClassrooms,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching
  };
}
function ClassroomCardsGrid({
  visibleItems,
  shouldShowCreateCard,
  shouldShowEnterCard,
  onCreateClassroom,
  onEnterClassroom,
  onEditClassroomName,
  onDeleteClassroom,
  onShareClassroom
}: {
  visibleItems: ClassroomItem[];
  shouldShowCreateCard: boolean;
  shouldShowEnterCard: boolean;
  onCreateClassroom: () => void;
  onEnterClassroom: () => void;
  onEditClassroomName: (item: ClassroomItem) => void;
  onDeleteClassroom: (item: ClassroomItem) => void;
  onShareClassroom: (item: ClassroomItem) => void;
}) {
  return <div className="grid w-full grid-cols-1 justify-center gap-md mobile-lg:grid-cols-[repeat(auto-fill,minmax(350px,1fr))] mobile-lg:justify-start mobile-lg:gap-x-md mobile-lg:gap-y-md" data-sentry-component="ClassroomCardsGrid" data-sentry-source-file="ClassroomListPageView.tsx">
      {shouldShowCreateCard ? <div className="min-w-0 max-mobile-lg:order-last">
          <CreateClassroomCard onClick={onCreateClassroom} />
        </div> : null}
      {/* 모바일 학생 시안(1284:26210)은 클래스 카드 뒤에 '클래스 입장' 카드를 둔다. */}
      {shouldShowEnterCard ? <div className="min-w-0 max-mobile-lg:order-last">
          <EnterClassroomCard onClick={onEnterClassroom} />
        </div> : null}
      {visibleItems.map(item => <div key={item.id} className="min-w-0">
          {item.isOwner ? <OwnerClassroomCard item={item} onDelete={onDeleteClassroom} onEditName={onEditClassroomName} onShare={onShareClassroom} /> : <StudentClassroomCard item={item} />}
        </div>)}
    </div>;
}
function ClassroomListEmptyState({
  activeTab,
  onCreateClassroom
}: {
  activeTab: ClassroomListTab;
  onCreateClassroom?: () => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const isOwnerTab = activeTab === ClassroomListTab.OWNED_CLASSROOMS;
  return <div className="group flex flex-col items-center py-2xl text-center" data-sentry-component="ClassroomListEmptyState" data-sentry-source-file="ClassroomListPageView.tsx">
      <img src="/assets/no_class.webp" alt="" className="h-[110px] w-[232px] object-contain transition-transform duration-300 group-hover:-translate-y-1.5" />
      <div className="mt-md flex flex-col items-center gap-3xs py-4xs">
        <p className="text-heading-md-bold text-default">
          {isOwnerTab ? t('classroom:classroomList.emptyOwner.title') : t('classroom:classroomList.emptyJoined.title')}
        </p>
        <p className="text-body-md-medium text-alternative">
          {isOwnerTab ? t('classroom:classroomList.emptyOwner.description') : t('classroom:classroomList.emptyJoined.description')}
        </p>
      </div>
      <div className="mt-md flex gap-xs">
        <Button variant="outlined" intent="alternative" size="lg" rounded="full" asChild data-sentry-element="Button" data-sentry-source-file="ClassroomListPageView.tsx">
          <Link href="/join" target="_blank" rel="noopener noreferrer" data-sentry-element="Link" data-sentry-source-file="ClassroomListPageView.tsx">
            {t('classroom:classroomList.joinWithCode')}
          </Link>
        </Button>
        {isOwnerTab && onCreateClassroom && <Button variant="contained" intent="strong" size="lg" rounded="full" leftSlot={<PlusIcon size="md" />} className="bg-gradient-mint-green" onClick={onCreateClassroom}>
            {t('classroom:classroomList.createClassroom')}
          </Button>}
      </div>
    </div>;
}
function getVisibleItems({
  activeTab,
  ownedClassrooms,
  joinedClassrooms
}: {
  activeTab: ClassroomListTab;
  ownedClassrooms: ClassroomItem[];
  joinedClassrooms: ClassroomItem[];
}) {
  if (activeTab === ClassroomListTab.OWNED_CLASSROOMS) {
    return ownedClassrooms;
  }
  return joinedClassrooms;
}
function getErrorStatus(error: unknown) {
  if (error && typeof error === 'object' && 'status' in error) {
    return String(error.status);
  }
  return undefined;
}
function ClassroomListHeader() {
  const {
    t,
    i18n
  } = useTranslation(['classroom']);
  return <>
      <div className="relative">
        <ProfileTitleWithMenus title={<h1 className="text-heading-xl-extrabold text-default">
              {formatEnglishTitleCase(t('classroom:classroomList.pageTitle'), i18n.resolvedLanguage)}
            </h1>} className="min-w-0 py-xs" hideMobileDropdown data-sentry-element="ProfileTitleWithMenus" data-sentry-source-file="ClassroomListPageView.tsx" />
      </div>
    </>;
}
export function ClassroomListPageView() {
  const {
    t
  } = useTranslation(['classroom']);
  const router = useRouter();
  const {
    isMobile
  } = useViewport();
  // 클래스를 가진 선생님이 랜딩하는 곳 → 포인트 출시 안내 자동 노출(계정당 1회).
  usePointReleaseModal();
  const deleteClassroom = useDeleteClassroom();
  const shouldShowOwnedClassroomsFirst = useCanCreateClassroom();
  // 학생 계정은 클래스 owner 여도 '만든 클래스' 탭과 owner 카드를 보여주지 않는다.
  // 학생이 만든 클래스는 제품 persona 밖 예외라, 계속 노출하면 학생관리/포인트 권한 분기가 꼬인다.
  const showOwnedTab = shouldShowOwnedClassroomsFirst;
  const {
    activeTab,
    tabs,
    handleChangeTab
  } = useDashboardTabState(showOwnedTab, shouldShowOwnedClassroomsFirst);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortMode, setSortMode] = useState<ClassroomListSortMode>('latest');
  const hasTrackedViewRef = useRef(false);
  const {
    ownedClassrooms,
    joinedClassrooms,
    isLoading,
    isFetching
  } = useClassroomItems({
    activeTab,
    searchText,
    sortMode
  });
  const visibleItems = getVisibleItems({
    activeTab,
    ownedClassrooms,
    joinedClassrooms
  });
  useEffect(() => {
    if (isLoading || hasTrackedViewRef.current) return;
    trackClassroomEvent('view_classroom_list', {
      source: 'classroom_list',
      activeTab,
      ownedCount: ownedClassrooms.length,
      joinedCount: joinedClassrooms.length,
      canCreateClassroom: shouldShowOwnedClassroomsFirst
    });
    hasTrackedViewRef.current = true;
  }, [activeTab, isLoading, joinedClassrooms.length, ownedClassrooms.length, shouldShowOwnedClassroomsFirst]);
  const handleShareClassroom = async (item: ClassroomItem) => {
    trackClassroomEvent('click_classroom_invite', {
      source: 'classroom_list',
      classroomId: item.id
    });
    try {
      const {
        data
      } = await getClassroomDetail(item.id);
      openClassroomShareModal({
        classroom: {
          id: data.classGroupId,
          code: data.classCode,
          name: data.name
        }
      });
    } catch {
      confirmDialog.open({
        header: {
          title: t('classroom:classroomList.errorTitle')
        },
        body: t('classroom:classroomList.shareLoadError'),
        confirmText: t('classroom:classroomList.confirm')
      });
    }
  };
  const openEditClassroomNameModal = (item: ClassroomItem) => {
    overlay.open(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <EditClassroomNameModal classroomId={item.id} name={item.name} onSubmitTrack={name => trackClassroomEvent('submit_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id,
        nameLength: name.length
      })} onSuccessTrack={() => trackClassroomEvent('success_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id
      })} onErrorTrack={error => trackClassroomEvent('fail_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id,
        errorStatus: getErrorStatus(error)
      })} onClose={close} />;
    });
  };
  const handleDeleteClassroom = async (item: ClassroomItem) => {
    trackClassroomEvent('click_delete_classroom', {
      source: 'classroom_list',
      classroomId: item.id
    });
    const confirmed = await confirmDialog.openAsync({
      header: {
        title: t('classroom:classroomList.deleteDialog.title')
      },
      body: t('classroom:classroomList.deleteDialog.body', {
        name: item.name
      }),
      confirmText: t('classroom:classroomList.deleteDialog.confirm'),
      confirmIntent: 'destructive',
      cancelText: t('classroom:classroomList.deleteDialog.cancel')
    });
    if (!confirmed) return;
    deleteClassroom.mutate(item.id, {
      onSuccess: () => {
        trackClassroomEvent('success_delete_classroom', {
          source: 'classroom_list',
          classroomId: item.id
        });
      },
      onError: () => {
        trackClassroomEvent('fail_delete_classroom', {
          source: 'classroom_list',
          classroomId: item.id
        });
        toast.error(t('classroom:classroomList.deleteError'));
      }
    });
  };
  const openCreateClassroomModal = async () => {
    trackClassroomEvent('click_create_classroom', {
      source: 'classroom_list',
      activeTab
    });
    trackClassroomEvent('view_create_classroom_modal', {
      source: 'create_classroom_modal',
      activeTab
    });
    const created = await overlay.openAsync<CreatedClassroom | undefined>(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <CreateClassroomModal onClose={() => close(undefined)} onCreated={data => close(data)} onSubmitTrack={name => trackClassroomEvent('submit_create_classroom', {
        source: 'create_classroom_modal',
        nameLength: name.length
      })} onSuccessTrack={data => trackClassroomEvent('success_create_classroom', {
        source: 'create_classroom_modal',
        classroomId: data.classroomId,
        memberCount: data.memberCount
      })} onErrorTrack={error => trackClassroomEvent('fail_create_classroom', {
        source: 'create_classroom_modal',
        errorStatus: getErrorStatus(error)
      })} />;
    });
    if (!created) return;
    trackClassroomEvent('view_created_classroom_modal', {
      source: 'created_classroom_modal',
      classroomId: created.classroomId
    });
    const classroomId = await overlay.openAsync<string | undefined>(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <CreatedClassroomModal onClose={() => close(undefined)} {...created} onGoToClass={id => {
        trackClassroomEvent('click_enter_created_classroom', {
          source: 'created_classroom_modal',
          classroomId: id
        });
        close(id);
      }} />;
    });
    if (classroomId) {
      void router.push(CLASSROOM_ROUTES.detail(classroomId, true, {
        isMobile
      }));
    }
  };
  const handleEnterClassroom = () => {
    void router.push('/join');
  };
  const isEmpty = !isLoading && visibleItems.length === 0 && !searchText;
  // 만든 클래스 탭이 노출되는 경우(교사/비학생 owner)에만 탭 UI 를 보인다.
  const shouldShowClassroomTabs = showOwnedTab;
  const shouldShowCreateClassroomPrompt = shouldShowOwnedClassroomsFirst && activeTab === ClassroomListTab.OWNED_CLASSROOMS;
  return <div className="flex w-full flex-col gap-sm mobile-lg:gap-md" data-sentry-component="ClassroomListPageView" data-sentry-source-file="ClassroomListPageView.tsx">
      <ClassroomListHeader data-sentry-element="ClassroomListHeader" data-sentry-source-file="ClassroomListPageView.tsx" />

      <ClassroomListSearchToolbar leading={shouldShowClassroomTabs ? <ClassroomListTabs activeTab={activeTab} tabs={tabs} onChangeTab={handleChangeTab} /> : undefined} filteredItemCount={visibleItems.length} isFetchingSortOrder={isFetching} searchText={searchInput} sortMode={sortMode} compact={isMobile} onChangeSortMode={setSortMode} onSearchTextChange={setSearchInput} onSearchSubmit={value => setSearchText(value.trim())} data-sentry-element="ClassroomListSearchToolbar" data-sentry-source-file="ClassroomListPageView.tsx" />

      {isLoading ? <ClassroomListGridSkeleton /> : isEmpty ? activeTab === ClassroomListTab.OWNED_CLASSROOMS && shouldShowOwnedClassroomsFirst ? <ClassroomListEmptyState activeTab={activeTab} onCreateClassroom={openCreateClassroomModal} /> : <ClassroomListEmptyState activeTab={activeTab} /> : visibleItems.length === 0 && searchText ? <div className="flex flex-1 flex-col items-center justify-center gap-md py-2xl">
          <div className="flex flex-col items-center justify-center gap-xs">
            <img className="aspect-[3/2] h-[128px] w-[192px] shrink-0" src="/assets/empty_avatar.webp" alt="" />
            <span className="text-heading-md-bold text-default">
              &quot;{searchText}&quot;
            </span>
            <span className="text-heading-md-bold text-alternative">
              {t('classroom:classroomList.noSearchResult')}
            </span>
            {shouldShowCreateClassroomPrompt ? <span className="text-body-lg-medium text-assistive">
                {t('classroom:classroomList.noSearchResultSuggestion')}
              </span> : null}
          </div>
          {shouldShowCreateClassroomPrompt ? <Button intent="alternative" variant="outlined" size="lg" rounded="full" leftSlot={<PlusIcon size="md" />} onClick={openCreateClassroomModal}>
              {t('classroom:classroomList.createClassroom')}
            </Button> : null}
        </div> : <ClassroomCardsGrid visibleItems={visibleItems} shouldShowCreateCard={activeTab === ClassroomListTab.OWNED_CLASSROOMS && shouldShowOwnedClassroomsFirst} shouldShowEnterCard={activeTab === ClassroomListTab.JOINED_CLASSROOMS} onCreateClassroom={openCreateClassroomModal} onEnterClassroom={handleEnterClassroom} onDeleteClassroom={item => void handleDeleteClassroom(item)} onEditClassroomName={openEditClassroomNameModal} onShareClassroom={item => void handleShareClassroom(item)} />}
    </div>;
}